import type { CheckoutAttemptResult } from './checkoutLifecycleContract';

export type CheckoutResultMode = 'success' | 'cancelled';
export type CheckoutTerminalSyncState = 'error' | 'pending' | 'left';

const PUBLIC_PLAN_IDS = ['starter', 'plus', 'pro'] as const;
const PRICING_INTERVALS = ['monthly', 'yearly'] as const;
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CNY'] as const;
const LEGACY_CHECKOUT_SESSION_PATTERN = /^cs_(?:test_|live_)?[a-zA-Z0-9_]+$/;

type PublicPlanId = typeof PUBLIC_PLAN_IDS[number];
type PricingInterval = typeof PRICING_INTERVALS[number];
type Currency = typeof CURRENCIES[number];

function parsePlanLookupKey(value: string | null | undefined): {
    planId: PublicPlanId;
    interval: PricingInterval;
} | null {
    if (!value) return null;
    const match = /^(starter|plus|pro)_(monthly|yearly)$/.exec(value);
    if (!match) return null;
    return {
        planId: match[1] as PublicPlanId,
        interval: match[2] as PricingInterval,
    };
}

function safeCurrency(value: string | null | undefined): Currency | null {
    const normalized = value?.toUpperCase();
    return CURRENCIES.includes(normalized as Currency) ? normalized as Currency : null;
}

export function resolveCheckoutTerminalSyncState({
    mode,
    hadError,
    completed,
}: {
    mode: CheckoutResultMode;
    hadError: boolean;
    completed: boolean;
}): CheckoutTerminalSyncState {
    if (hadError) return 'error';
    if (completed || mode === 'success') return 'pending';
    return 'left';
}

export function buildCheckoutRetryHref({
    result,
    requestedPlanLookupKey,
    requestedCurrency,
    billing,
    requestedPreviewProjectId,
    requestedPreviewResultDigestId,
}: {
    result: CheckoutAttemptResult | null;
    requestedPlanLookupKey?: string;
    requestedCurrency?: string;
    billing?: PricingInterval;
    requestedPreviewProjectId?: string | null;
    requestedPreviewResultDigestId?: string | null;
}): string {
    const plan = parsePlanLookupKey(result?.planLookupKey ?? requestedPlanLookupKey);
    const resolvedBilling = plan?.interval ?? billing;
    if (requestedPreviewProjectId) {
        const params = new URLSearchParams();
        params.set('preview_project_id', requestedPreviewProjectId);
        if (requestedPreviewResultDigestId) {
            params.set('result_digest_id', requestedPreviewResultDigestId);
        }
        if (resolvedBilling) params.set('billing', resolvedBilling);
        return `/trial/preview${params.size ? `?${params.toString()}` : ''}`;
    }

    if (!plan) return '/pricing';

    const params = new URLSearchParams({
        checkout_plan: plan.planId,
        checkout_interval: plan.interval,
    });
    const currency = safeCurrency(requestedCurrency);
    if (currency) params.set('checkout_currency', currency);
    return `/pricing?${params.toString()}`;
}

export function parseLegacyCheckoutSessionId(value: string | undefined): string | null {
    const normalized = value?.trim();
    if (
        !normalized
        || normalized.length > 255
        || !LEGACY_CHECKOUT_SESSION_PATTERN.test(normalized)
    ) {
        return null;
    }
    return normalized;
}
